/**
 * <s-media-fieldset> — enables its child <fieldset> only while `data-media` matches.
 *
 * The collection page renders the same filter controls twice (desktop dropdown bar and
 * mobile drawer) inside one form. A disabled fieldset's controls are left out of FormData,
 * so only the copy for the current viewport submits and duplicate params never reach the URL.
 *
 * Attributes:
 *   data-media — media query that enables the fieldset, e.g. "(min-width: 768px)".
 */
class SMediaFieldset extends HTMLElement {
  #ac;

  connectedCallback() {
    const fieldset = this.querySelector('fieldset');
    const query = this.dataset.media;
    if (!fieldset || !query) return;

    this.#ac = new AbortController();
    const mq = window.matchMedia(query);
    const sync = () => {
      fieldset.disabled = !mq.matches;
    };
    sync();
    mq.addEventListener('change', sync, { signal: this.#ac.signal });

    // Close an open dropdown on Escape or an outside click, so the bar behaves like a menu.
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        const open = this.querySelector('details[open]');
        if (!open) return;
        open.removeAttribute('open');
        open.querySelector('summary')?.focus();
      },
      { signal: this.#ac.signal },
    );
    document.addEventListener(
      'click',
      (event) => {
        for (const open of this.querySelectorAll('details[open]')) {
          if (!open.contains(event.target)) open.removeAttribute('open');
        }
      },
      { signal: this.#ac.signal },
    );
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('s-media-fieldset')) customElements.define('s-media-fieldset', SMediaFieldset);
